import ValidationStatus from '../../Data/ValidationStatus';
import PHONE_CONSTRAINT_NAMES from '../../Data/phoneConstraintNames';
import POSTCODE_CONSTRAINT_NAMES from '../../Data/postcodeConstraintNames';
import { getComponentValidationMapping } from '../../Helpers/validation';
import externalSelectConfig from '../../Config/externalSelectConfig';
import { ComponentTypes, GenericAdmiralFields } from '../../Data/Components';

/**
 * This is a helper for the cpc cards and appointment booking
 * @param {*} param0
 * @returns config for the formId in the env the function is called
 */
export const getEnvConfigForFormId = ({ config, formId }) => {
  if (!config[process.env.NEXT_PUBLIC_ENV]) {
    return null;
  }
  if (!config[process.env.NEXT_PUBLIC_ENV][formId]) {
    return null;
  }
  return config[process.env.NEXT_PUBLIC_ENV][formId];
};

export const getMappedComponents = ({ config, components }) => {
  if (!config || !components) {
    return {};
  }

  // get the components defined in the config and key by admiral question ID
  return Object.values(components)
    .filter((component) =>
      Object.keys(config.questions).includes(
        component.component.mappedQuestionId
      )
    )
    .reduce((acc, component) => {
      return {
        ...acc,
        [component.component.mappedQuestionId]: component,
      };
    }, {});
};

export const getComponentByNumericalId = ({ components, componentId }) => {
  return components[`component_${componentId}`];
};

/**
 * Checks if all conditional questions from the config provided have been answered. This is used by appointment booking and cpc cards
 * @param {*} param0
 * @returns boolean
 */
export const areConditionalQuestionsAnswered = ({
  formId,
  components,
  config,
}) => {
  const envConfigForForm = getEnvConfigForFormId({
    config,
    formId,
  });

  if (!envConfigForForm) {
    return false;
  }

  const mappedComponents = getMappedComponents({
    config: envConfigForForm,
    components,
  });

  /**
   * Are ALL answers from the config present in the components?
   *
   * This is done by checking if all the values in the config are present in the components (and lower casing them to avoid case sensitivity issues)
   */
  return Object.entries(envConfigForForm.questions).every(
    ([mappedQuestionId, values]) => {
      const lowerCaseValues = Array.isArray(values)
        ? values.map((value) => value.toLowerCase())
        : values.toLowerCase();

      const component = mappedComponents[mappedQuestionId];

      return (
        lowerCaseValues.indexOf(
          component?.input?.value?.trim().toLowerCase()
        ) !== -1
      );
    }
  );
};

export const isEveryComponentValid = ({ components, countryCode }) => {
  let pageIsValid = true;

  if (!components) {
    return false;
  }

  Object.values(components).forEach((component) => {
    const componentValidationMappings = getComponentValidationMapping(
      countryCode,
      component
    );

    Object.keys(componentValidationMappings).forEach((constraint) => {
      if (!componentValidationMappings[constraint]) {
        pageIsValid = false;
      }
    });
  });

  return pageIsValid;
};

export const getComponentValidationStatus = ({ component }) => {
  const constraints = component?.validation?.constraints;

  if (!component.validation || !constraints) {
    return ValidationStatus.VALID; // if there are no constraints then the component is valid
  }

  const constraintValues = Object.values(constraints);
  if (
    constraintValues.every(
      (constraint) => constraint?.status === ValidationStatus.VALID
    )
  ) {
    return ValidationStatus.VALID; // if all are valid return valid
  }

  if (isAnyConstraintPendingValidation(constraints)) {
    return ValidationStatus.PENDING; // if there are any constraints pending return pending
  }

  if (
    constraintValues.some(
      (constraint) => constraint?.status === ValidationStatus.NOT_VALIDATED
    )
  ) {
    return ValidationStatus.NOT_VALIDATED;
  }

  if (
    constraintValues.every((constraint) => constraint?.status === undefined)
  ) {
    return ValidationStatus.NOT_VALIDATED; // there has been no validation attempt so return not validated
  }

  return ValidationStatus.INVALID; // else return invalid and find what went wrong in the constraints
};

/**
 * Checks if any constraints are pending validation
 *
 * @param {constraints} constraints
 * @returns boolean
 */
export const isAnyConstraintPendingValidation = (constraints) => {
  let isPending = false;

  Object.values(constraints).forEach((constraint) => {
    if (constraint.status === ValidationStatus.PENDING) {
      isPending = true;
    }
  });
  return isPending;
};

/**
 * Checks if any components have validation pending
 *
 * @param {components} components
 * @returns boolean
 */
export const isAnyComponentPendingValidation = ({ components }) => {
  let isPending = false;

  Object.values(components).forEach((component) => {
    if (component?.validation && component?.validation?.constraints) {
      isPending = isAnyConstraintPendingValidation(
        component.validation.constraints
      );
    }
  });

  return isPending;
};

export const isIconForEveryOption = (options) => {
  if (!Array.isArray(options)) {
    return false;
  }
  if (options.length === 0) {
    return false;
  }
  const urls = options.map((option) => option.icon_url).filter(Boolean);

  return options.length === urls.length;
};

export const swapS3DomainWithPublicAssetsDomain = (url, clientAssetsUrl) => {
  if (typeof url !== 'string') {
    return url;
  }

  if (url.startsWith('s3:')) {
    const s3DomainRegex = /^s3:\/\/(.+?)\//; // matches s3:// followed by a set of characters ending in a '/'
    const match = url.match(s3DomainRegex);

    if (match && match[1]) {
      const s3Domain = match[1];
      return url.replace(`s3://${s3Domain}`, clientAssetsUrl);
    }
  }
  return url;
};

export const isGoogleAddressComponent = (component) => {
  return component?.component?.type === 'google-address';
};

export const isLoaderComponent = (component) => {
  return component?.component?.type === 'loader';
};

export const hasPhoneConstraint = ({ component }) =>
  component?.validation?.constraints &&
  PHONE_CONSTRAINT_NAMES.some(
    (constraintName) => component.validation?.constraints[constraintName]
  );

export const hasPostcodeConstraint = ({ component }) =>
  component?.validation?.constraints &&
  POSTCODE_CONSTRAINT_NAMES.some(
    (constraintName) => component.validation?.constraints[constraintName]
  );

export const isAnyPhoneNumberQuestionFilled = (components) => {
  return Object.values(components).some(
    (component) => hasPhoneConstraint({ component }) && !!component.input.value
  );
};

export const includesPhoneOrPostcode = ({ components }) => {
  let result = false;

  Object.values(components).forEach((component) => {
    if (
      hasPhoneConstraint({ component }) ||
      hasPostcodeConstraint({ component })
    ) {
      result = true;
    }
  });
  return result;
};

export const getSupplierMatchComponent = ({ components }) => {
  return Object.values(components).find(
    (component) => component.component?.type === 'supplier-match'
  );
};

export const getPostcodeComponent = ({ components }) => {
  return Object.values(components).find(
    (component) =>
      component.component.mappedQuestionId === 'primary_address_postalcode' ||
      component.component.mappedQuestionId === 'postcode'
  );
};

export const getFirstNameComponent = ({ components }) => {
  return Object.values(components).find(
    (component) =>
      component.component.mappedQuestionId === 'first_name' ||
      component.component.mappedQuestionId === 'your_first_name'
  );
};

export const isEmailComponent = ({ component }) => {
  return component.component.mappedQuestionId === GenericAdmiralFields.EMAIL;
};

export const getEmailComponent = ({ components }) => {
  return Object.values(components).find(
    (component) =>
      component.component.mappedQuestionId === GenericAdmiralFields.EMAIL
  );
};

export const getLastNameComponent = ({ components }) => {
  return Object.values(components).find(
    (component) => component.component.mappedQuestionId === 'last_name'
  );
};

export const getDefaultNewsletterName = ({ components }) => {
  const firstNewsletterComponent = Object.values(components).find(
    (component) =>
      component?.component?.type === ComponentTypes.NEWSLETTER_SIGNUP
  );
  if (!firstNewsletterComponent) {
    return undefined;
  }
  if (
    firstNewsletterComponent?.component?.additionalSettings?.brand === 'none'
  ) {
    return undefined;
  }
  return firstNewsletterComponent?.component?.additionalSettings?.brand;
};

export const getPhoneNumberComponent = ({ components }) => {
  return Object.values(components).find(
    (component) =>
      component.component.mappedQuestionId === 'phone_work' ||
      component.component.mappedQuestionId === 'phone_mobile' ||
      component.component.mappedQuestionId === 'mobile_number' ||
      component.component.mappedQuestionId === 'phone_home' ||
      component.component.mappedQuestionId === 'phone_office'
  );
};

/**
 * @returns {boolean} true if the component has a mappedQuestionId, all required mappedQuestionIds are used in the form, and the relevant questions are set up to be selects
 */
export const isExternalSelect = (components, mappedQuestionId) => {
  const requiredMappedQuestionIds = Object.keys(
    externalSelectConfig[process.env.NEXT_PUBLIC_ENV]
  );

  if (
    !mappedQuestionId ||
    !requiredMappedQuestionIds.includes(mappedQuestionId)
  ) {
    return false;
  }

  const mappedQuestionIdsUsedInForm = Object.values(components)
    .filter((formComponent) => {
      return formComponent?.input?.type === 'select';
    })
    .map((formComponent) => {
      return formComponent?.component?.mappedQuestionId;
    })
    .filter((mappedQuestionIdUsedInForm) => {
      return requiredMappedQuestionIds.includes(mappedQuestionIdUsedInForm);
    });

  return (
    mappedQuestionIdsUsedInForm.length === requiredMappedQuestionIds.length
  );
};

export const isPhoneAndPostcodeFilled = (visiblePageComponents) => {
  let postcodeFieldFilled = false;
  let phoneFieldFilled = false;

  let hasPostcodeComponent = false;
  let hasPhoneComponent = false;

  Object.values(visiblePageComponents).forEach((component) => {
    if (hasPostcodeConstraint({ component })) {
      hasPostcodeComponent = true;
      if (component?.input?.value?.length > 0) {
        postcodeFieldFilled = true;
      }
    }

    if (hasPhoneConstraint({ component })) {
      hasPhoneComponent = true;
      if (component?.input?.value?.length > 0) {
        phoneFieldFilled = true;
      }
    }
  });

  if (hasPostcodeComponent && hasPhoneComponent) {
    return postcodeFieldFilled && phoneFieldFilled;
  }

  return postcodeFieldFilled || phoneFieldFilled;
};
